import React, { useMemo } from "react";
import { Atom } from "lucide-react";
import { AGENT_COLORS } from "../utils/tools";
import { LogsList } from "./LogsList";

export function AgentAnalyzingPanel({
  activeAgents,
  isAnalyzing,
  progress,
  analysisComplete,
  progressExpanded,
  onToggleExpand,
  progressPanelRef,
  completedAgentsCount,
  isDockOpen = false,
}) {
  const orchestratorAccent = "#EC10A3";
  const derivedCompletedAgentsCount = Array.isArray(progress)
    ? progress.filter(
        (log) =>
          log?.kind === "agent-status" && log?.meta?.state === "completed"
      ).length
    : 0;

  const effectiveCompletedCount =
    typeof completedAgentsCount === "number"
      ? completedAgentsCount
      : derivedCompletedAgentsCount;

  const summaryText = analysisComplete
    ? `${effectiveCompletedCount} agent${effectiveCompletedCount === 1 ? "" : "s"} done`
    : `${Math.max(activeAgents.length || 1, 1)} agent${
        Math.max(activeAgents.length || 1, 1) === 1 ? "" : "s"
      } running`;

  const planPreview = useMemo(() => {
    if (!Array.isArray(progress)) return null;
    const firstPlan = progress.find((p) => p?.kind === "plan-paragraph");
    if (firstPlan?.text) {
      const trimmed = firstPlan.text.trim();
      return trimmed.length > 120 ? `${trimmed.slice(0, 117)}…` : trimmed;
    }
    return null;
  }, [progress]);

  const hasPlan = useMemo(() => {
    if (!Array.isArray(progress)) return false;
    return progress.some(
      (p) => p?.kind === "plan-paragraph" || p?.kind === "plan-heading"
    );
  }, [progress]);

  const headerStatus = hasPlan ? summaryText : "Thinking…";

  return (
    <div
      style={{
        margin: "0 0 0.75rem 0",
        borderRadius: 0,
        background: "#000000",
        border: "none",
        boxShadow: "none",
        width: "100%",
        maxWidth: "1100px",
        minWidth: 0,
        minHeight: "auto",
        overflow: "visible",
        backdropFilter: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 10,
          padding: "6px 0",
          cursor: "pointer",
        }}
        onClick={onToggleExpand}
      >
        <Atom size={18} style={{ color: "#FFFFFF" }} />
        <span
          className="agent-analysis-label"
          style={{
            fontStyle: "Inter",
            fontWeight: 400,
            fontSize: 14,
            lineHeight: "100%",
            color: "#e5e7eb",
            display: "inline-block",
            marginRight: 6,
          }}
        >
          {isAnalyzing ? "AI Agents Are Analyzing" : "Agent Analysis"}
        </span>
        <span
          style={{
            fontSize: 12,
            color: "#9ca3af",
            whiteSpace: "nowrap",
            textShadow: `0 0 12px ${orchestratorAccent}33`,
          }}
        >
          {headerStatus}
        </span>
        {(progress.length > 0 || analysisComplete) && (
          <div
            style={{
              transform: progressExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              color: "#9ca3af",
              marginLeft: 8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path
                d="M4.5 6L8 9.5L11.5 6"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
          </div>
        )}
      </div>

      {progressExpanded && progress.length > 0 && (
        <div
          ref={progressPanelRef}
          style={{
            maxHeight: isDockOpen ? "360px" : "350px",
            padding: "12px 16px 14px 16px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            overflowY: "scroll",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            background: "#000000",
            borderTop: "1px solid rgba(255,255,255,0.04)",
          }}
        >
          <LogsList logs={progress} analysisComplete={analysisComplete} />
        </div>
      )}
    </div>
  );
}
